import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DiagnosisTagGlyph } from '@/components/shared/DiagnosisTagChips';
import { Button } from '@/components/ui/button';
import { TableCell, TableHead, TableRow } from '@/components/ui/table';
import { mergeClinicalTags } from '@/lib/clinicalTags';
import { formatDate, formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ArrowUpDown, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ModalityBadge } from './ModalityBadge';
import { NotifyIcons } from './WorklistNotifyIcons';
import { PatientMetaLine, PatientNameLine } from './WorklistPatientCell';
// The one placeholder for a missing value on this surface (rendered in
// text-muted-foreground). Never "-", "--", or a bold word posing as data.
export const EMPTY_VALUE = '—';
/**
 * Fixed column widths (table-layout: fixed, so their sum is the table's
 * minimum width). Notify is sized for its glyph cluster: two glyphs plus the
 * stethoscope button (59px) inside 4px cell padding. At 60px the cluster stuck
 * out of its column, so the table was always a few pixels wider than its
 * scroller (a sideways wobble on touch, a stray scrollbar on Windows).
 */
export const EXPANDER_COLUMN_WIDTH = 32;
export const NOTIFY_COLUMN_WIDTH = 68;
const COLUMN_WIDTH = {
    pid: 104,
    patientName: 172,
    description: 128,
    dateTime: 110,
    modality: 68,
    series: 52,
    images: 60,
    accession: 112,
    institution: 84,
};
const COLUMN_SORT = {
    patientName: 'PatientName',
    description: 'Description',
    dateTime: 'StudyDate',
    modality: 'Modality',
    accession: 'AccessionNumber',
    institution: 'Institution',
};
const CENTERED_COLUMNS = new Set(['modality', 'series', 'images']);
function SortButton({ field, children, onSort, }) {
    return (_jsxs(Button, { variant: "ghost", size: "sm", className: "text-muted-foreground hover:text-foreground data-[state=open]:bg-accent -ml-2 h-7 px-2 text-[10px] font-medium", onClick: (e) => {
            e.stopPropagation();
            onSort(field);
        }, children: [children, _jsx(ArrowUpDown, { className: "ml-1 h-3 w-3" })] }));
}
export function WorklistHeaderCell({ columnKey, onSort, }) {
    const { t } = useTranslation();
    const sortField = COLUMN_SORT[columnKey];
    const label = t(`worklist.col.${columnKey}`);
    return (_jsx(TableHead, { className: cn('text-muted-foreground/70 text-[9px] font-semibold tracking-wider uppercase', CENTERED_COLUMNS.has(columnKey) && 'text-center'), style: { width: COLUMN_WIDTH[columnKey] }, children: sortField ? (_jsx(SortButton, { field: sortField, onSort: onSort, children: label })) : (label) }));
}
/** One collapsed worklist row (sm and up; phones get WorklistCardList). */
export function WorklistTableRow({ study, display, columns, showNotifyColumn, isExpanded, isReceiving, isDragging, detailsId, onRowClick, onToggle, onHover, onHoverEnd, onDragStart, onDragEnd, onOpenViewer, onOpenConsultation, onPrefetch, }) {
    const { t } = useTranslation();
    const renderCell = (key) => {
        switch (key) {
            case 'pid':
                return (_jsx(TableCell, { className: "text-sm", children: display.patientIdRecorded ? (_jsx("span", { title: display.patientId, className: "text-foreground/90 inline-block max-w-full align-bottom font-mono text-xs font-semibold break-all", children: `#${display.patientIdDisplay}` })) : (_jsx("span", { className: "text-muted-foreground", "data-testid": `study-pid-not-recorded-${study.id}`, children: t('worklist.notRecorded') })) }, key));
            case 'patientName':
                return (_jsx(TableCell, { children: _jsxs("div", { className: "min-w-0", children: [_jsx(PatientNameLine, { study: study, name: display.patientName, isStat: display.isStat, onOpenKeyImages: onOpenViewer }), _jsx(PatientMetaLine, { study: study, name: display.patientName, isReceiving: isReceiving }), _jsx(DiagnosisTagGlyph, { tags: mergeClinicalTags(study.tags, study.studyTags), className: "mt-0.5" })] }) }, key));
            case 'description':
                return (_jsx(TableCell, { className: "text-muted-foreground text-sm italic", children: study.study.description || EMPTY_VALUE }, key));
            case 'dateTime':
                return (_jsx(TableCell, { children: study.study.dateTime ? (_jsxs("div", { children: [_jsx("div", { className: "text-foreground text-sm", children: formatDate(study.study.dateTime, {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                }) }), _jsx("div", { className: "text-muted-foreground/60 font-mono text-[11px]", children: formatTime(study.study.dateTime, {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                }) })] })) : (_jsx("span", { className: "text-muted-foreground", children: EMPTY_VALUE })) }, key));
            case 'modality':
                return (_jsx(TableCell, { className: "text-center", children: _jsx(ModalityBadge, { modality: study.study.modality, modalities: study.study.modalities }) }, key));
            case 'series':
                return (_jsx(TableCell, { className: "text-foreground text-center text-sm font-medium", children: study.counts.seriesCount ?? (_jsx("span", { className: "text-muted-foreground", children: EMPTY_VALUE })) }, key));
            case 'images':
                return (_jsx(TableCell, { className: "text-foreground text-center text-sm font-medium", children: study.counts.imageCount ?? (_jsx("span", { className: "text-muted-foreground", children: EMPTY_VALUE })) }, key));
            case 'accession':
                return (_jsx(TableCell, { className: "text-muted-foreground truncate font-mono text-xs", title: study.study.accessionNumber || undefined, children: study.study.accessionNumber || EMPTY_VALUE }, key));
            case 'institution':
                return (_jsx(TableCell, { className: "text-muted-foreground truncate text-xs", title: display.institutionLabel || undefined, children: display.institutionLabel || EMPTY_VALUE }, key));
            default:
                return null;
        }
    };
    return (_jsxs(TableRow, { className: cn('cursor-pointer border-b border-[#1E293B] transition-colors hover:bg-white/[0.02]', isExpanded && 'bg-white/[0.03]', display.isStat && 'bg-red-950/20', isDragging && 'opacity-50'), onClick: onRowClick, onMouseEnter: onHover, onMouseLeave: onHoverEnd, "data-testid": `study-row-${study.id}`, draggable: true, onDragStart: onDragStart, onDragEnd: onDragEnd, children: [_jsx(TableCell, { className: cn(display.isStat && 'border-l-2 border-l-red-500', display.isUrgent && 'border-l-2 border-l-yellow-500'), children: _jsx("button", { type: "button", onClick: (e) => {
                        e.stopPropagation();
                        onToggle();
                    }, "aria-label": t('worklist.toggleDetails', { name: display.patientName }), "aria-expanded": isExpanded, "aria-controls": isExpanded ? detailsId : undefined, "data-testid": `study-expander-${study.id}`, className: "hover:bg-muted focus-visible:ring-ring relative rounded p-1 before:absolute before:-inset-2 focus-visible:ring-2 focus-visible:outline-none", children: _jsx(ChevronDown, { "aria-hidden": true, className: cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180') }) }) }), columns.map(renderCell), showNotifyColumn && (_jsx(TableCell, { style: { padding: '0 4px' }, onMouseEnter: onPrefetch, children: _jsx(NotifyIcons, { study: study, onOpenConsultation: onOpenConsultation }) }))] }));
}
